import React from "react";
import styled from "styled-components";
import Img from "gatsby-image";
import { useStaticQuery, graphql } from "gatsby";

// Responsive breakpoints for hero images
export const BREAKPOINTS = {
  mobile: { max: 440 },
  desktop800: { min: 441, max: 899 },
  desktop1200: { min: 900, max: 1399 },
  desktop1600: { min: 1400, max: 1799 },
  desktop2400: { min: 1800 },
} as const;

// Image paths for hero images
export const HERO_IMAGE_PATHS = {
  desktop2400: "hero-images/familyFull-2400.webp",
  desktop1600: "hero-images/familyFull-1600.webp",
  desktop1200: "hero-images/familyFull-1200.webp",
  desktop800: "hero-images/familyFull-800.webp",
  mobile800: "hero-images/familyMobile-800.webp",
} as const;

export const HeroImage = () => {
  // Note: GraphQL queries cannot use template literals, so we use hardcoded strings
  // that match the HERO_IMAGE_PATHS constant for consistency
  const data = useStaticQuery(graphql`
    query {
      desktopImage2400: file(relativePath: { eq: "hero-images/familyFull-2400.webp" }) {
        childImageSharp {
          fluid(maxWidth: 2400, quality: 90) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      desktopImage1600: file(relativePath: { eq: "hero-images/familyFull-1600.webp" }) {
        childImageSharp {
          fluid(maxWidth: 1600, quality: 90) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      desktopImage1200: file(relativePath: { eq: "hero-images/familyFull-1200.webp" }) {
        childImageSharp {
          fluid(maxWidth: 1200, quality: 90) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      desktopImage800: file(relativePath: { eq: "hero-images/familyFull-800.webp" }) {
        childImageSharp {
          fluid(maxWidth: 800, quality: 90) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
      mobileImage800: file(relativePath: { eq: "hero-images/familyMobile-800.webp" }) {
        childImageSharp {
          fluid(maxWidth: 800, quality: 90) {
            ...GatsbyImageSharpFluid_withWebp
          }
        }
      }
    }
  `);

  // Desktop images
  const desktopImage2400 = data.desktopImage2400?.childImageSharp?.fluid;
  const desktopImage1600 = data.desktopImage1600?.childImageSharp?.fluid;
  const desktopImage1200 = data.desktopImage1200?.childImageSharp?.fluid;

  // Mobile images
  const desktopImage800 = data.desktopImage800?.childImageSharp?.fluid;
  const mobileImage800 = data.mobileImage800?.childImageSharp?.fluid;

  return (
    <HeroImageWrapper>
      <div className="hero">
        <div className="hero__bg">
          <Img
            fluid={desktopImage2400}
            alt="Happy family together"
            className="hero__bg__img hero__bg__img--desktop-2400"
            loading="eager"
            critical={true}
            fadeIn={false}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center top",
              zIndex: 0,
            }}
          />
          <Img
            fluid={desktopImage1600}
            alt="Happy family together"
            className="hero__bg__img hero__bg__img--desktop-1600"
            loading="eager"
            critical={true}
            fadeIn={false}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center top",
              zIndex: 0,
            }}
          />
          <Img
            fluid={desktopImage1200}
            alt="Happy family together"
            className="hero__bg__img hero__bg__img--desktop-1200"
            loading="eager"
            critical={true}
            fadeIn={false}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center top",
              zIndex: 0,
            }}
          />
          <Img
            fluid={desktopImage800}
            alt="Happy family together"
            className="hero__bg__img hero__bg__img--desktop-800"
            loading="eager"
            critical={true}
            fadeIn={false}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center top",
              zIndex: 0,
            }}
          />
          <Img
            fluid={mobileImage800}
            alt="Happy family together"
            className="hero__bg__img hero__bg__img--mobile"
            loading="eager"
            critical={true}
            fadeIn={false}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center top",
              zIndex: 0,
            }}
          />
        </div>
      </div>
      <div className="hero__overlay"></div>
    </HeroImageWrapper>
  );
};

const HeroImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 666px;
  overflow: hidden;

  .hero {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .hero__bg,
  .hero__bg__img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center top;
    z-index: 0;
    image-rendering: -webkit-optimize-contrast;
    image-rendering: crisp-edges;
    backface-visibility: hidden;
  }

  .hero__bg__img--desktop-2400 {
    display: none;
    @media (min-width: ${BREAKPOINTS.desktop2400.min}px) {
      display: block;
    }
  }

  .hero__bg__img--desktop-1600 {
    display: none;
    @media (min-width: ${BREAKPOINTS.desktop1600.min}px) and (max-width: ${BREAKPOINTS.desktop1600.max}px) {
      display: block;
    }
  }

  .hero__bg__img--desktop-1200 {
    display: none;
    @media (min-width: ${BREAKPOINTS.desktop1200.min}px) and (max-width: ${BREAKPOINTS.desktop1200.max}px) {
      display: block;
    }
  }

  .hero__bg__img--desktop-800 {
    display: none;
    @media (min-width: ${BREAKPOINTS.desktop800.min}px) and (max-width: ${BREAKPOINTS.desktop800.max}px) {
      display: block;
    }
  }

  /* Mobile Images */
  .hero__bg__img--mobile {
    display: none;
    @media (max-width: ${BREAKPOINTS.mobile.max}px) {
      display: block;
    }
  }

  .hero__overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(12deg, rgba(0, 0, 0, 0.5) 39%, rgba(0, 0, 0, 0) 56%);
    z-index: 1;
  }

  .gatsby-image-wrapper img {
    object-fit: cover !important;
    object-position: top center !important;
    transform: none !important; /* prevents extra scaling */
  }

  @media (max-width: ${BREAKPOINTS.mobile.max}px) {
    height: 903px;
    .hero__bg {
      margin-top: 42px;
    }
  }
`;
